import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { companyInfo } from '../mock';
import { useToast } from '../hooks/use-toast';

export const ContactPage = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    serviceType: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock submission - will be replaced with actual API call
    console.log('Form submitted:', formData);
    toast({
      title: "Message envoyé !",
      description: "Nous vous contacterons dans les plus brefs délais.",
    });
    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      serviceType: '',
      message: ''
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-gray-50 to-white py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Contactez-nous
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Une question ? Un projet ? N'hésitez pas à nous contacter pour un devis gratuit et sans engagement
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Demander un devis</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name">Nom complet *</Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="mt-2"
                    placeholder="Votre nom"
                  />
                </div>

                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="mt-2"
                    placeholder="votre@email.ch"
                  />
                </div>

                <div>
                  <Label htmlFor="phone">Téléphone *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="mt-2"
                    placeholder="+41 XX XXX XX XX"
                  />
                </div>

                <div>
                  <Label htmlFor="serviceType">Type de travaux *</Label>
                  <Select
                    value={formData.serviceType}
                    onValueChange={(value) => setFormData({ ...formData, serviceType: value })}
                  >
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="Sélectionnez un service" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="platrerie-peinture">Plâtrerie - Peinture</SelectItem>
                      <SelectItem value="isolation">Isolation extérieure & intérieure</SelectItem>
                      <SelectItem value="carrelage">Carrelage</SelectItem>
                      <SelectItem value="sanitaire">Sanitaire</SelectItem>
                      <SelectItem value="toiture">Toiture</SelectItem>
                      <SelectItem value="nettoyage">Nettoyage</SelectItem>
                      <SelectItem value="demolition">Démolition & débarras</SelectItem>
                      <SelectItem value="autre">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="message">Votre message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    className="mt-2"
                    rows={6}
                    placeholder="Décrivez-nous votre projet..."
                  />
                </div>

                <Button type="submit" size="lg" className="w-full bg-gray-800 hover:bg-gray-700 text-white">
                  Envoyer le message
                  <Send className="ml-2" size={20} />
                </Button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Nos coordonnées</h2>
                <p className="text-gray-600 leading-relaxed mb-8">
                  Vous préférez nous appeler directement ? Nous sommes à votre disposition pour répondre à toutes vos questions.
                </p>
              </div>

              <Card className="border border-gray-200">
                <CardHeader>
                  <CardTitle className="flex items-center text-gray-900">
                    <Phone className="mr-3 text-gray-700" size={24} />
                    Téléphone
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <a
                    href={`tel:${companyInfo.phone}`}
                    className="block text-lg text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    {companyInfo.phone}
                  </a>
                </CardContent>
              </Card>

              <Card className="border border-gray-200">
                <CardHeader>
                  <CardTitle className="flex items-center text-gray-900">
                    <Mail className="mr-3 text-gray-700" size={24} />
                    Email
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <a
                    href={`mailto:${companyInfo.email}`}
                    className="text-lg text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    {companyInfo.email}
                  </a>
                </CardContent>
              </Card>

              <Card className="border border-gray-200">
                <CardHeader>
                  <CardTitle className="flex items-center text-gray-900">
                    <MapPin className="mr-3 text-gray-700" size={24} />
                    Zone d'intervention
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Valais, canton de Vaud et régions limitrophes
                  </p>
                </CardContent>
              </Card>

              <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-3">Horaires</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Lundi - Vendredi : 8h00 - 18h00<br />
                  Samedi : 9h00 - 12h00<br />
                  Dimanche : Fermé
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};